import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { cn } from './cn';

type Div = HTMLAttributes<HTMLDivElement>;

/** 아바타 — 온라인 상태 점, 크기 프리셋 */
export function Avatar({
  src,
  name,
  size = 44,
  online,
  className,
}: {
  src?: string;
  name: string;
  size?: number;
  online?: boolean;
  className?: string;
}) {
  const initials = name.trim().slice(0, 2).toUpperCase() || '?';
  return (
    <span
      className={cn('relative inline-block shrink-0', className)}
      style={{ width: size, height: size }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          className="h-full w-full rounded-full object-cover"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span
          className="flex h-full w-full items-center justify-center rounded-full bg-[var(--accent)] text-white"
          style={{ fontSize: size * 0.36 }}
          aria-hidden
        >
          {initials}
        </span>
      )}
      {online !== undefined && (
        <span
          className={cn(
            'absolute -right-0.5 -bottom-0.5 rounded-full border-2 border-[var(--bg-list)]',
            online ? 'bg-[var(--accent)]' : 'bg-[var(--text-secondary)]',
          )}
          style={{ width: size * 0.28, height: size * 0.28 }}
          role="img"
          aria-label={online ? '온라인' : '오프라인'}
        />
      )}
    </span>
  );
}

/** 아이콘 버튼 — 최소 44px 터치 영역 보장 */
export function IconButton({
  label,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn('tap-target pressable text-[var(--text-secondary)]', className)}
      {...rest}
    >
      {children}
    </button>
  );
}

/** 눌림 피드백 래퍼 */
export function Pressable({ children, className, ...rest }: Div & { children: ReactNode }) {
  return (
    <div className={cn('pressable', className)} {...rest}>
      {children}
    </div>
  );
}

/** 온/오프 토글 — 접근성 속성 포함 */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn('pressable toggle-switch', checked && 'toggle-switch--checked')}
    >
      <span className="toggle-thumb" aria-hidden="true" />
    </button>
  );
}

/** 하단 시트 — 모바일 전용 액션 컨테이너 (PLAN 6-1장) */
export function BottomSheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{
        background: 'var(--overlay)',
        zIndex: 'var(--z-sheet, 1000)',
      }}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[480px] rounded-t-[var(--radius-lg)] bg-[var(--bg-list)] pb-[max(var(--safe-bottom),16px)] relative"
        style={{
          animation: 'sheet-up var(--duration-base) var(--ease-ios)',
          maxHeight: '85dvh',
          overflowY: 'auto',
          zIndex: 'var(--z-sheet, 1000)',
        }}
      >
        <div className="mx-auto my-2 h-1 w-10 rounded-full bg-[var(--text-secondary)]/40" />
        {title && (
          <h2 className="px-4 pt-1 pb-3 text-center text-sm text-[var(--text-secondary)]">
            {title}
          </h2>
        )}
        {children}
      </div>
      <style>{`@keyframes sheet-up{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
    </div>
  );
}

/** 간단 토스트 — 낙관적 업데이트 실패/되돌리기 안내용 */
export function Toast({
  message,
  actionLabel,
  onAction,
  durationMs = 4000,
}: {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  durationMs?: number;
}) {
  useEffect(() => {
    const t = window.setTimeout(() => undefined, durationMs);
    return () => window.clearTimeout(t);
  }, [durationMs]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 bottom-[calc(var(--safe-bottom)+var(--space-6))] flex -translate-x-1/2 items-center gap-3 rounded-[var(--radius-md)] bg-black/85 px-4 py-3 text-sm text-white shadow-lg"
      style={{ zIndex: 'var(--z-toast, 1100)' }}
    >
      <span>{message}</span>
      {actionLabel && onAction && (
        <button type="button" onClick={onAction} className="font-semibold text-[#53bdeb]">
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Signature Stripe 밴드 (PLAN 6-2A장, Paul Smith).
 *
 * 쓰이는 곳은 **3곳뿐**이다 — 온보딩 배경 / 빈 대화방 / 아이콘 테두리.
 * 그 밖에 쓰면 6-1-3(버블만 색을 가진다)을 무너뜨리므로 리뷰에서 반려한다.
 *
 * 색·폭 배열은 `tokens.css`에 있고 여기서는 마크업만 낸다. 두 곳에서 배열을
 * 따로 들고 있으면 언젠가 한쪽만 바뀌어 정체를 잃는다.
 *
 * 장식이 아니라 **로딩/빈 상태 신호**로 읽히도록 `aria-hidden`을 기본으로 둔다.
 * 스크린리더에 "분홍, 남색, 빨강…"을 읽게 하는 것이 이 продук의 의도가 아니다.
 */
export function StripeBand({
  variant = 'band',
  className,
}: {
  variant?: 'band' | 'banner';
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn(variant === 'band' ? 'stripe-band' : 'stripe-banner', className)}
    >
      {/* 고정 8색 + 초과분 확장 1색. 배열 길이는 tokens.css의 nth-child 규칙과 맞춰야 한다. */}
      {Array.from({ length: 9 }, (_, i) => (
        <i key={i} />
      ))}
    </div>
  );
}

/**
 * Magic UI 스타일 Border Beam 컴포넌트 (PLAN UI_UX.md Module 2.1).
 * 카드의 테두리를 따라 에메랄드/스카이블루 그라디언트 빔이 순환한다.
 */
export function BorderBeam({
  size = 140,
  duration = 8,
  delay = 0,
  colorFrom,
  colorTo,
  className,
}: {
  size?: number;
  duration?: number;
  delay?: number;
  colorFrom?: string;
  colorTo?: string;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn('border-beam-container', className)}
      style={
        {
          '--beam-size': `${size}px`,
          '--beam-duration': `${duration}s`,
          '--beam-delay': `-${delay}s`,
          ...(colorFrom ? { '--beam-from': colorFrom } : {}),
          ...(colorTo ? { '--beam-to': colorTo } : {}),
        } as React.CSSProperties
      }
    />
  );
}

/**
 * Magic UI 스타일 Avatar Circles (PLAN UI_UX.md Module 2.3 / 3.3).
 * 그룹 대화방 참여자 아바타를 중첩하여 표시한다.
 */
export function AvatarCircles({
  numPeople,
  avatarUrls,
  max = 3,
  size = 28,
  className,
}: {
  numPeople?: number;
  avatarUrls: Array<{ src?: string; name: string }>;
  max?: number;
  size?: number;
  className?: string;
}) {
  const visible = avatarUrls.slice(0, max);
  const totalCount = numPeople ?? avatarUrls.length;
  const remaining = totalCount > max ? totalCount - max : 0;

  return (
    <div className={cn('flex items-center -space-x-2 overflow-hidden', className)}>
      {visible.map((u, i) => (
        <span
          key={i}
          className="relative inline-block shrink-0 rounded-full ring-2 ring-[var(--bg-list)]"
          style={{ width: size, height: size }}
        >
          {u.src ? (
            <img
              src={u.src}
              alt=""
              width={size}
              height={size}
              className="h-full w-full rounded-full object-cover"
              loading="lazy"
            />
          ) : (
            <span
              className="flex h-full w-full items-center justify-center rounded-full bg-[var(--accent)] text-white font-medium"
              style={{ fontSize: size * 0.38 }}
            >
              {u.name.trim().slice(0, 2).toUpperCase() || '?'}
            </span>
          )}
        </span>
      ))}
      {remaining > 0 && (
        <span
          className="flex shrink-0 items-center justify-center rounded-full bg-[var(--divider)] font-semibold text-[var(--text-secondary)] ring-2 ring-[var(--bg-list)]"
          style={{ width: size, height: size, fontSize: size * 0.35 }}
        >
          +{remaining}
        </span>
      )}
    </div>
  );
}

/**
 * Magic UI 스타일 숫자 티커 / 배지 카운터 (PLAN UI_UX.md Module 2.3).
 */
export function NumberTicker({ value, className }: { value: number; className?: string }) {
  return (
    <span className={cn('inline-block tabular-nums transition-all duration-300', className)}>
      {value}
    </span>
  );
}

/**
 * Magic UI 스타일 Bento Grid 레이아웃 (PLAN UI_UX.md Module 5.1).
 */
export function BentoGrid({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('grid w-full grid-cols-1 md:grid-cols-2 gap-3', className)}>{children}</div>
  );
}

/**
 * Magic UI 스타일 Bento Card 컴포넌트 (PLAN UI_UX.md Module 5.1).
 */
export function BentoCard({
  children,
  className,
  title,
  icon,
  badge,
}: {
  children: React.ReactNode;
  className?: string;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'group relative flex flex-col justify-between overflow-hidden rounded-[var(--radius-lg)] border border-[var(--divider)] bg-[var(--bg-surface-elevated)] p-4 shadow-sm transition-all hover:shadow-md hover:border-[var(--accent)]/40',
        className,
      )}
    >
      {(title || icon || badge) && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {icon && <span className="text-base">{icon}</span>}
            {title && (
              <h4 className="text-xs font-bold tracking-wide uppercase text-[var(--text-secondary)]">
                {title}
              </h4>
            )}
          </div>
          {badge && <div>{badge}</div>}
        </div>
      )}
      <div className="flex-1">{children}</div>
    </div>
  );
}

/**
 * 모던 Squircle 그라디언트 아이콘 뱃지 (PLAN UI_rev2.md 3.1장).
 * Mobbin / iOS / Linear 스타일의 부드러운 스퀘어클 아이콘 컨테이너.
 */
export type SquircleGradient =
  'blue' | 'emerald' | 'purple' | 'amber' | 'cyan' | 'rose' | 'red' | 'slate';

const GRADIENT_MAP: Record<SquircleGradient, string> = {
  blue: 'bg-linear-to-br from-blue-600 to-indigo-700',
  emerald: 'bg-linear-to-br from-emerald-600 to-teal-700',
  purple: 'bg-linear-to-br from-purple-600 to-violet-700',
  amber: 'bg-linear-to-br from-amber-600 to-orange-700',
  cyan: 'bg-linear-to-br from-cyan-600 to-sky-700',
  rose: 'bg-linear-to-br from-rose-600 to-pink-700',
  red: 'bg-linear-to-br from-red-600 to-rose-700',
  slate: 'bg-linear-to-br from-slate-600 to-zinc-700',
};

export function SquircleBadge({
  gradient = 'blue',
  children,
  size = 40,
  className,
}: {
  gradient?: SquircleGradient;
  children: React.ReactNode;
  size?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-2xl text-white shadow-sm ring-1 ring-white/20 transition-transform group-hover:scale-105',
        GRADIENT_MAP[gradient],
        className,
      )}
      style={{ width: size, height: size }}
    >
      {children}
    </div>
  );
}

/**
 * Inset Grouped 컨테이너 (PLAN UI_rev2.md 3.3장).
 * Linear Mobile / iOS 18 세팅 스타일의 둥근 모서리 카드 그룹.
 */
export function InsetGroup({
  children,
  title,
  className,
}: {
  children: React.ReactNode;
  title?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {title && (
        <h3 className="px-4 text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">
          {title}
        </h3>
      )}
      <div className="overflow-hidden rounded-2xl border border-[var(--divider)]/80 bg-[var(--bg-surface-elevated)] shadow-sm divide-y divide-[var(--divider)]/50">
        {children}
      </div>
    </div>
  );
}

/**
 * Inset Grouped 항목 (버튼 또는 링크 컨테이너).
 */
export function InsetItem({
  children,
  onClick,
  disabled,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group flex w-full items-center gap-3.5 px-4 py-3.5 text-left transition-all active:bg-[var(--divider)]/30 active:scale-[0.99] disabled:opacity-50 disabled:pointer-events-none',
        className,
      )}
    >
      {children}
    </button>
  );
}
